import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { Wildfire } from 'features/wildfires/wildfire.types';
import * as Yup from 'yup';
import { isSupportedEmbedUrl } from 'shared/embedHelpers';
import {
  getRichTextFieldRequiredValidation,
  parseNumericString,
  parsePhoneNumberString,
} from 'shared/utils';
import { AnyObject } from 'yup/lib/types';
import ObjectSchema, { AssertsShape, TypeOfShape } from 'yup/lib/object';
import { getReportMessageValidationSchema } from 'app/pages/AddIncidentReport/ReportForm/utils';
import { isValid } from 'date-fns';
import { PhoneNumberRegex, UrlRegex } from '../../../../../constants';

export const validatePrescribedDateStartLocal = (input: {
  date: Date | null;
  initialDate?: Date;
}): boolean => {
  const { date, initialDate } = input;
  if (!date) return true;
  if (!isValid(date)) return false;
  if (initialDate) {
    if (date.setSeconds(0, 0) < initialDate.setSeconds(0, 0)) {
      // When editing an incident's prescribed start date, ensure that if the new date
      // is earlier than the initial date, it still remains in the future relative
      // to the current date
      return date.setSeconds(0, 0) > new Date().setSeconds(0, 0);
    }
    return true;
  }
  return date.setSeconds(0, 0) > new Date().setSeconds(0, 0);
};

const useValidationSchema = (
  geoEvent: Wildfire | undefined,
  regionHasEvacZones: boolean,
  t: TFunction,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ObjectSchema<any, AnyObject, TypeOfShape<any>, AssertsShape<any>> => {
  return useMemo(
    () =>
      Yup.object().shape(
        {
          name: Yup.string().required(
            t('createEditGeoEvent.inputs.name.required'),
          ),
          address: Yup.string().when('isComplexParent', {
            is: true,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required(
              t('createEditGeoEvent.inputs.name.required'),
            ),
          }),
          regions: Yup.array().when('isComplexParent', {
            is: true,
            then: Yup.array().min(0),
            otherwise: Yup.array().min(
              1,
              t('createEditGeoEvent.inputs.counties.required'),
            ),
          }),
          status: Yup.string().required(
            t('createEditGeoEvent.inputs.status.required'),
          ),
          notificationType: Yup.string().required(
            t('createEditGeoEvent.inputs.notificationType.required'),
          ),
          acreage: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .typeError(t('createEditGeoEvent.inputs.acreage.required'))
            .min(0, t('createEditGeoEvent.inputs.acreage.invalidError'))
            .max(9999999, t('createEditGeoEvent.inputs.acreage.invalidError'))
            .nullable(),
          containment: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .typeError(t('createEditGeoEvent.inputs.containment.required'))
            .min(0, t('createEditGeoEvent.inputs.containment.invalidError'))
            .max(100, t('createEditGeoEvent.inputs.containment.invalidError'))
            .integer()
            .nullable(),
          isComplexParent: Yup.boolean(),
          isFps: Yup.boolean(),
          isPrescribed: Yup.boolean(),
          evacuationOrders: Yup.string().when('customOrders', {
            is: (customOrders: boolean) =>
              regionHasEvacZones && Boolean(customOrders),
            then: getRichTextFieldRequiredValidation(
              t('geoEventEvacuations.inputs.evacuationOrders.required'),
            ),
            otherwise: Yup.string().nullable(),
          }),
          evacuationWarnings: Yup.string().when('customWarnings', {
            is: (customWarnings: boolean) =>
              regionHasEvacZones && Boolean(customWarnings),
            then: getRichTextFieldRequiredValidation(
              t('geoEventEvacuations.inputs.evacuationWarnings.required'),
            ),
            otherwise: Yup.string().nullable(),
          }),
          evacuationAdvisories: Yup.string().when('customAdvisories', {
            is: (customAdvisories: boolean) =>
              regionHasEvacZones && Boolean(customAdvisories),
            then: getRichTextFieldRequiredValidation(
              t('geoEventEvacuations.inputs.evacuationAdvisory.required'),
            ),
            otherwise: Yup.string().nullable(),
          }),
          evacuationNotes: Yup.string().nullable(),
          reportNotificationType: Yup.string(),
          reportMessage: Yup.string().when([], {
            is: () => !geoEvent?.id,
            then: getReportMessageValidationSchema(t),
            otherwise: Yup.string(),
          }),
          lat: Yup.number(),
          lng: Yup.number(),
          prescribedDateStartLocal: Yup.mixed()
            .nullable()
            .test(
              'is-future-date',
              t('createEditGeoEvent.inputs.prescribedDateStart.invalidError'),
              (value) => {
                return validatePrescribedDateStartLocal({
                  date: value,
                  initialDate: geoEvent?.data.prescribedDateStartLocal
                    ? new Date(geoEvent.data.prescribedDateStartLocal)
                    : undefined,
                });
              },
            ),
          activeEvacuations: Yup.boolean(),
          evacZoneStatuses: Yup.array(),
          confirmSelectedZones: Yup.boolean().when('confirmSelectedZones', {
            is: (exist: boolean) => typeof exist === 'boolean',
            then: Yup.boolean().oneOf([true]),
            otherwise: Yup.boolean(),
          }),
          customOrders: Yup.boolean(),
          customWarnings: Yup.boolean(),
          customAdvisories: Yup.boolean(),
          asset: Yup.string().nullable(),
          media: Yup.mixed().nullable(),
          fileType: Yup.string().nullable(),
          embedUrl: Yup.string()
            .url(t('addIncidentReport.inputs.embedUrl.invalidError'))
            .test(
              'is-supported-embed-url',
              t('addIncidentReport.inputs.embedUrl.unsupportedError'),
              (value) => (value ? isSupportedEmbedUrl(value) : true),
            ),
          mediaLat: Yup.number()
            .typeError(t('addIncidentReport.inputs.latitude.typeError'))
            .min(-90, t('addIncidentReport.inputs.latitude.minError'))
            .max(90, t('addIncidentReport.inputs.latitude.maxError'))
            .when('mediaLng', {
              is: (mediaLng: string) => Boolean(mediaLng),
              then: Yup.number()
                .transform(parseNumericString)
                .nullable()
                .required(t('addIncidentReport.inputs.latitude.required')),
              otherwise: Yup.number().transform(parseNumericString).nullable(),
            }),
          mediaLng: Yup.number()
            .typeError(t('addIncidentReport.inputs.longitude.typeError'))
            .min(-180, t('addIncidentReport.inputs.longitude.minError'))
            .max(180, t('addIncidentReport.inputs.longitude.maxError'))
            .when('mediaLat', {
              is: (mediaLat: string) => Boolean(mediaLat),
              then: Yup.number()
                .transform(parseNumericString)
                .nullable()
                .required(t('addIncidentReport.inputs.longitude.required')),
              otherwise: Yup.number().transform(parseNumericString).nullable(),
            }),
          mediaAz: Yup.number()
            .typeError(t('addIncidentReport.inputs.azimuth.typeError'))
            .min(0, t('addIncidentReport.inputs.azimuth.minError'))
            .max(360, t('addIncidentReport.inputs.azimuth.maxError')),
          links: Yup.array(
            Yup.object().shape({
              linkType: Yup.string()
                .required(t('formLinks.inputs.linkType.required'))
                .nullable(),
              label: Yup.string().required(
                t('formLinks.inputs.linkLabel.required'),
              ),
              value: Yup.string()
                .when('linkType', (linkType, schema) => {
                  if (linkType === 'email') {
                    return schema.email(
                      t('formLinks.inputs.linkValue.invalid.email'),
                    );
                  }
                  if (linkType === 'website') {
                    return schema.matches(
                      UrlRegex,
                      t('formLinks.inputs.linkValue.invalid.url'),
                    );
                  }
                  if (linkType === 'phone') {
                    return schema
                      .transform(parsePhoneNumberString)
                      .matches(
                        PhoneNumberRegex,
                        t('formLinks.inputs.linkValue.invalid.phoneNumber'),
                      );
                  }
                  return schema;
                })
                .required(t('formLinks.inputs.linkValue.required')),
            }),
          ),
          reporterOnlyNotes: Yup.string().nullable(),
          childWildfires: Yup.array().when('isComplexParent', {
            is: true,
            then: Yup.array().min(
              1,
              t('createEditGeoEvent.inputs.complexIncidents.required'),
            ),
            otherwise: Yup.array().max(0),
          }),
        },
        [
          ['mediaLat', 'mediaLng'],
          ['confirmSelectedZones', 'confirmSelectedZones'],
        ],
      ),
    [
      geoEvent?.data.prescribedDateStartLocal,
      geoEvent?.id,
      regionHasEvacZones,
      t,
    ],
  );
};

export default useValidationSchema;
